import { useEffect, useState } from 'react';
import { parseEntityRef, type Entity } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { techdocsApiRef } from '@backstage/plugin-techdocs-react';
import { TECHDOCS_REF } from './ComponentPageContext';

export const TECHDOCS_ENTITY = 'backstage.io/techdocs-entity';
export const TECHDOCS_ENTITY_PATH = 'backstage.io/techdocs-entity-path';

/** The TechDocs site a component's documentation is, as the reader addresses it. */
export type TechDocsSite = {
  kind: string;
  namespace: string;
  name: string;
  /** The page of it the component names, below the site's root. */
  path?: string;
};

/**
 * Whose documentation a component has: its own, or — for a subcomponent whose
 * docs are part of its main component's — the entity it names, and the page
 * in them (DX-628). Read the way the Docs tab's reader reads the annotations,
 * so the tab, the Overview's rows and the scorecard open the same site: the
 * reference names its kind, its namespace is `default` unless it names one,
 * and a reference without a kind is not followed.
 */
export function techDocsOf(entity: Entity): TechDocsSite | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const namespace = entity.metadata.namespace ?? 'default';
  const target = annotations[TECHDOCS_ENTITY]?.trim();
  if (target) {
    try {
      const ref = parseEntityRef(target);
      const path = annotations[TECHDOCS_ENTITY_PATH]?.trim().replace(
        /^\/+|\/+$/g,
        '',
      );
      return {
        kind: ref.kind.toLowerCase(),
        namespace: ref.namespace.toLowerCase(),
        name: ref.name,
        ...(path ? { path } : {}),
      };
    } catch {
      // Not a reference: the reader falls back to the component's own docs.
    }
  }
  if (!annotations[TECHDOCS_REF]) return undefined;
  return {
    kind: entity.kind.toLowerCase(),
    namespace: namespace.toLowerCase(),
    name: entity.metadata.name,
  };
}

export type RunbookPage = {
  title: string;
  /** Where it opens in the reader, relative to the entity's docs root. */
  location: string;
  /** How many more runbook pages the docs hold beyond this one. */
  more: number;
};

export type TechDocsFacts = {
  loading: boolean;
  error?: Error;
  /** When the docs were last built, where the build recorded it. */
  builtAt?: Date;
  /** The docs are declared but no build of them exists yet. */
  notBuilt?: boolean;
  runbook?: RunbookPage;
};

type SearchDoc = { location: string; title: string; text: string };

/**
 * A page's own heading as MkDocs indexes it: the text before the first
 * permalink sign ("AI Gateway Runbooks ¶ Audience: …"). The heading, not the
 * first sentence, because a runbook opens on metadata — audience, scope,
 * endpoint, owner — and its first real sentence is already about one alert.
 */
export function headingOf(page: SearchDoc): string {
  const [heading, ...rest] = page.text.replace(/<[^>]+>/g, ' ').split('¶');
  // No permalink sign, no heading: the text is body, and the title is all
  // there is to go on.
  if (!rest.length) return page.title;
  const text = heading.replace(/\s+/g, ' ').trim();
  // "Runbook: Backstage MCP Server" under a row already titled Runbook: the
  // prefix only pushes the "+1 more" out of sight.
  return text.replace(/^runbooks?\s*[:—–-]\s*/i, '') || page.title;
}

/**
 * A runbook is a page whose path starts with "runbook" — "runbooks/" too. The
 * first one is the row's target; the others are counted, so none is hidden.
 *
 * A subcomponent that names a runbook page of its main component's site has
 * that page as its runbook, and the site's others are not its own to count.
 */
export function runbookOf(
  docs: SearchDoc[],
  path?: string,
): RunbookPage | undefined {
  if (path && /^runbook/i.test(path)) {
    // A section of the page names the page; the row opens the section.
    const [named, section] = path.split('#');
    const page = docs.find(
      doc => doc.location.replace(/\/+$/, '') === named.replace(/\/+$/, ''),
    );
    return page
      ? {
          title: headingOf(page),
          location: section ? `${page.location}#${section}` : page.location,
          more: 0,
        }
      : undefined;
  }
  const pages = docs.filter(
    doc => !doc.location.includes('#') && /^runbook/i.test(doc.location),
  );
  if (!pages.length) return undefined;
  return {
    title: headingOf(pages[0]),
    location: pages[0].location,
    more: pages.length - 1,
  };
}

/**
 * What the Overview says about a component's documentation: when it was last
 * built, and whether it has a runbook (specs/component-page.md). Only asked for
 * a component that has TechDocs at all.
 */
export function useTechDocsFacts(entity: Entity): TechDocsFacts {
  const techdocsApi = useApi(techdocsApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const hasDocs = Boolean(techDocsOf(entity));
  const [state, setState] = useState<TechDocsFacts>({ loading: hasDocs });

  useEffect(() => {
    const site = techDocsOf(entity);
    if (!site) {
      setState({ loading: false });
      return undefined;
    }
    let active = true;
    setState({ loading: true });
    const { path, ...id } = site;
    (async () => {
      try {
        const metadata = (await techdocsApi.getTechDocsMetadata(id)) as {
          build_timestamp?: number;
        };
        let runbook: RunbookPage | undefined;
        try {
          const base = await discoveryApi.getBaseUrl('techdocs');
          const res = await fetchApi.fetch(
            `${base}/static/docs/${id.namespace}/${id.kind}/${id.name}/search/search_index.json`,
          );
          if (res.ok) {
            const index = (await res.json()) as { docs?: SearchDoc[] };
            runbook = runbookOf(index.docs ?? [], path);
          }
        } catch {
          // No search index: the docs still exist, there is just no runbook row.
        }
        if (active) {
          setState({
            loading: false,
            // Milliseconds: the TechDocs build writes Date.now().
            builtAt: metadata.build_timestamp
              ? new Date(metadata.build_timestamp)
              : undefined,
            runbook,
          });
        }
      } catch (err) {
        if (!active) return;
        // The metadata of a build that never ran is a 404: not an error of
        // this page, and not one worth the reader's alarm.
        if (
          (err as { response?: { status?: number } }).response?.status === 404
        )
          setState({ loading: false, notBuilt: true });
        else setState({ loading: false, error: err as Error });
      }
    })();
    return () => {
      active = false;
    };
  }, [entity, techdocsApi, discoveryApi, fetchApi]);

  return state;
}
